import { useMemo } from "react";
import { RiHistoryLine } from "react-icons/ri";
import { MenuSelect, Text } from "../../elements";
import styles from "./autocomplete-search-results.module.css";
import type { AutocompleteGroup, AutocompleteItem } from "./autocomplete.types";
import { useAutocomplete } from "./hooks";

export function AutocompleteSearchResults() {
  const {
    defaultOptions,
    inputValue,
    filteredOptions,
    hasResults,
    variant,
    close,
    onChange,
  } = useAutocomplete();

  function printWithHighlights(word: string) {
    const normalizeString = (str: string) =>
      str.normalize("NFD").replace(/[\u0300-\u036f]/g, "");

    const normalizedWord = normalizeString(word);
    const normalizedInput = normalizeString(inputValue);
    const regex = new RegExp(normalizedInput, "gi");

    let startIndex = 0;
    return normalizedWord.replace(regex, (match) => {
      startIndex = normalizedWord.indexOf(match, startIndex);
      const originalMatch = word.slice(startIndex, startIndex + match.length);
      return `<b>${originalMatch}</b>`;
    });
  }

  function handleChange(item: AutocompleteItem) {
    close();

    if (onChange) {
      onChange({
        ...item,
        searchTerm: inputValue,
      });
    }
  }

  const displayDefaultOptions = useMemo(() => {
    if (defaultOptions.length === 0) {
      return false;
    }

    if (inputValue.length > 0 && hasResults) {
      return false;
    }

    return true;
  }, [defaultOptions, inputValue, hasResults]);

  if (!defaultOptions.length && inputValue === "") {
    return;
  }

  return (
    <div className={styles.Container} data-variant={variant}>
      <MenuSelect>
        <MenuSelect.Body>
          {!hasResults && (
            <MenuSelect.Item selectable={false}>
              <div className={styles.Item}>
                <Text variant="body-bold-small" color="#6A6977">
                  Nenhum resultado encontrado
                </Text>
              </div>
            </MenuSelect.Item>
          )}

          {displayDefaultOptions && (
            <MenuSelect.Section>
              <MenuSelect.Title>Buscas recentes</MenuSelect.Title>
              {defaultOptions.map((item: AutocompleteItem) => (
                <MenuSelect.Item
                  onClick={() => handleChange(item)}
                  key={item.value}
                >
                  <div className={styles.Item}>
                    <div className={styles.Icon}>
                      <RiHistoryLine size={20} />
                    </div>
                    <Text variant="body-regular-small">{item.label}</Text>
                  </div>
                </MenuSelect.Item>
              ))}
            </MenuSelect.Section>
          )}

          {!displayDefaultOptions &&
            filteredOptions.map((option: AutocompleteGroup) => (
              <MenuSelect.Section key={option.label}>
                <MenuSelect.Title>{option.label}</MenuSelect.Title>
                {option.items?.map((item) => (
                  <MenuSelect.Item
                    onClick={() => handleChange(item)}
                    key={item.value}
                  >
                    <div className={styles.Item} data-variant="no-icon">
                      <Text
                        variant="body-regular-small"
                        dangerouslySetInnerHTML={{
                          __html: printWithHighlights(item.label),
                        }}
                      />
                    </div>
                  </MenuSelect.Item>
                ))}
              </MenuSelect.Section>
            ))}
        </MenuSelect.Body>
      </MenuSelect>
    </div>
  );
}
